import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { EMP } from "../constants/testIds";
import { ArrowLeft, IdentificationCard, EnvelopeSimple, Phone, Buildings, Briefcase, MapPin, CalendarBlank, Drop, ShieldWarning, House, Bank } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import EmployeeEditDialog from "../components/EmployeeEditDialog";
import { useAuth } from "../context/AuthContext";
import { Cake } from "@phosphor-icons/react";

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-t border-zinc-100 first:border-t-0">
      <Icon size={16} weight="duotone" className="text-emerald-700 mt-0.5" />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-900">{value || "—"}</div>
      </div>
    </div>
  );
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const [emp, setEmp] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const { user } = useAuth();
  const canEdit = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const load = () => {
    api.get(`/employees/${id}`).then(({ data }) => setEmp(data));
    api.get("/attendance", { params: { employee_id: id } }).then(({ data }) => setAttendance(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!emp) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid={EMP.detailRoot} className="space-y-6">
      <div>
        <Link to="/app/employees" className="text-xs text-zinc-500 hover:text-emerald-700 inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to employees</Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {emp.passport_photo_path ? (
              <img src={emp.passport_photo_path} alt={`${emp.first_name} ${emp.last_name}`}
                className="w-20 h-24 rounded-sm object-cover border border-zinc-200"
                data-testid="emp-detail-photo" />
            ) : (
              <div className="w-16 h-16 rounded-sm bg-emerald-700 text-white grid place-items-center font-display text-2xl font-bold">
                {(emp.first_name?.[0] || "") + (emp.last_name?.[0] || "")}
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">{emp.employee_code}</div>
              <h1 className="font-display text-4xl font-bold tracking-tight">{emp.first_name} {emp.last_name}</h1>
              <div className="text-sm text-zinc-500 mt-1">{emp.designation} · {emp.department}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && <EmployeeEditDialog emp={emp} onSaved={load} />}
            <Badge className={`rounded-sm ${emp.status === "active" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-zinc-100 text-zinc-700 border border-zinc-200"}`} variant="outline">
              {emp.status}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4 lg:col-span-1">
          <div className="border border-zinc-200 bg-white rounded-sm p-5">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Profile</div>
            <Row icon={IdentificationCard} label="Employee code" value={emp.employee_code} />
            <Row icon={EnvelopeSimple} label="Email" value={emp.email} />
            <Row icon={Phone} label="Mobile number" value={emp.phone} />
            <Row icon={Buildings} label="Department" value={emp.department} />
            <Row icon={Briefcase} label="Designation / Grade" value={`${emp.designation || "—"}${emp.grade ? " · " + emp.grade : ""}`} />
            <Row icon={MapPin} label="Branch / Location" value={emp.location} />
            <Row icon={CalendarBlank} label="Date of joining" value={emp.date_of_joining} />
            <Row icon={Cake} label="Date of birth" value={emp.date_of_birth} />
            <Row icon={IdentificationCard} label="Gender / PT state" value={[emp.gender, emp.pt_state].filter(Boolean).join(" · ")} />
            {emp.skill_tags?.length > 0 && (
              <div className="pt-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Skills</div>
                <div className="flex flex-wrap gap-1.5">
                  {emp.skill_tags.map((s) => (
                    <Badge key={s} variant="outline" className="rounded-sm border-zinc-300 bg-zinc-50 text-zinc-800">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="emp-personal-block">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Personal &amp; emergency</div>
            <Row icon={Drop} label="Blood group" value={emp.blood_group} />
            <Row icon={Bank} label="UAN (PF)" value={emp.uan} />
            <Row icon={ShieldWarning} label="Emergency contact"
              value={emp.emergency_contact_name
                ? `${emp.emergency_contact_name}${emp.emergency_contact_relation ? " · " + emp.emergency_contact_relation : ""}${emp.emergency_contact_phone ? " · " + emp.emergency_contact_phone : ""}`
                : null} />
            <Row icon={House} label="Permanent address" value={emp.permanent_address} />
            <Row icon={House} label="Current address" value={emp.current_address} />
          </div>
        </div>

        <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-sm p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Attendance history</div>
              <div className="font-display text-xl font-bold mt-1">Recent check-ins</div>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="hr-table">
              <thead>
                <tr><th>Date</th><th>Check-in</th><th>Check-out</th><th>Hours</th><th>Geo</th></tr>
              </thead>
              <tbody>
                {attendance.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-zinc-500 text-sm">No records yet.</td></tr>}
                {attendance.map((a) => (
                  <tr key={a.id}>
                    <td className="font-mono-plex text-xs">{a.date}</td>
                    <td className="font-mono-plex text-xs">{a.check_in_at ? new Date(a.check_in_at).toLocaleTimeString() : "—"}</td>
                    <td className="font-mono-plex text-xs">{a.check_out_at ? new Date(a.check_out_at).toLocaleTimeString() : "—"}</td>
                    <td className="font-mono-plex text-xs">{a.hours_worked ?? "—"}</td>
                    <td>
                      {a.check_in_within_geofence ? (
                        <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">On-site</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm bg-orange-50 text-orange-700 border-orange-200">Off-site</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
